import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Handshake, Users, Clock, CheckCircle2, XCircle, Wallet, Ticket, CreditCard,
  Trash2, Plus, Save, ArrowUp, ArrowDown, Sparkles, Eye, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const getAuth = () => {
  const t = localStorage.getItem("admin_token") || localStorage.getItem("token") || "";
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export default function AdminAgentApplications() {
  const [tab, setTab] = useState("applications");

  return (
    <div className="p-4 lg:p-6 min-h-screen bg-[#0a1119] text-white" data-testid="admin-agent-apps">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
          <Handshake className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-black">Agent Applications</h1>
          <p className="text-xs text-white/50 mt-0.5">Review submissions · manage coupons · order payment gateways</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/10">
        {[
          { id: "applications", label: "Applications", icon: Users },
          { id: "coupons",      label: "Coupons",      icon: Ticket },
          { id: "gateways",     label: "Gateway Settings", icon: CreditCard },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              tab === id ? "border-emerald-400 text-emerald-300" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid={`tab-${id}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "applications" && <ApplicationsTab />}
      {tab === "coupons"      && <CouponsTab />}
      {tab === "gateways"     && <GatewaysTab />}
    </div>
  );
}

/* ═════════════════════════ Applications Tab ═════════════════════════ */
function ApplicationsTab() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        axios.get(`${BACKEND}/api/admin/agent-applications/stats`, getAuth()),
        axios.get(`${BACKEND}/api/admin/agent-applications${filter ? `?status=${filter}` : ""}`, getAuth()),
      ]);
      setStats(s.data);
      setApps(a.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    const note = status === "rejected" ? (window.prompt("Reason for rejection (optional):") || "") : "";
    try {
      await axios.post(`${BACKEND}/api/admin/agent-applications/${id}/status`, { status, admin_note: note }, getAuth());
      toast.success(`Application ${status}`);
      load();
      if (viewing?.id === id) setViewing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const cards = stats ? [
    { label: "Total applied", value: stats.total, icon: Users, tone: "sky" },
    { label: "Payment pending", value: stats.payment_pending, icon: Clock, tone: "amber" },
    { label: "Payment paid", value: stats.paid, icon: Wallet, tone: "emerald" },
    { label: "Pending review", value: stats.pending_review, icon: Eye, tone: "amber" },
    { label: "Approved", value: stats.approved, icon: CheckCircle2, tone: "emerald" },
    { label: "Rejected", value: stats.rejected, icon: XCircle, tone: "red" },
    { label: "Collected (BDT)", value: `৳${Number(stats.total_collected_bdt || 0).toLocaleString("en-IN")}`, icon: Sparkles, tone: "emerald" },
  ] : [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-white/70">Overview</div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white" data-testid="apps-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2">
        {["", "payment_pending", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === s ? "bg-emerald-500 text-black border-emerald-400" : "bg-white/[0.03] text-white/70 border-white/10 hover:border-white/25"
            }`}
            data-testid={`filter-${s || "all"}`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/5 bg-[#0f1620] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Applicant</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-white/40">No applications found</td></tr>
              ) : apps.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-white">{a.name}</div>
                    <div className="text-[11px] text-white/40">{a.city}{a.state ? `, ${a.state}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white/80">{a.email}</div>
                    <div className="text-[11px] text-white/40">{a.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <PaymentBadge status={a.payment_status} />
                    <div className="text-[11px] text-white/40 mt-1">৳{Number(a.final_amount || a.paid_amount || 13000).toLocaleString("en-IN")}</div>
                    <div className="text-[10px] text-white/30 uppercase">{a.gateway_method}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-3 text-[11px] text-white/50">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setViewing(a)} className="text-xs px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 mr-1" data-testid={`view-${a.id}`}>View</button>
                    {a.status === "pending" && a.payment_status === "paid" && (
                      <>
                        <button onClick={() => updateStatus(a.id, "approved")}
                                className="text-xs px-2.5 py-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold mr-1"
                                data-testid={`approve-${a.id}`}>Approve</button>
                        <button onClick={() => updateStatus(a.id, "rejected")}
                                className="text-xs px-2.5 py-1 rounded-full bg-red-500/80 hover:bg-red-500 text-white font-bold"
                                data-testid={`reject-${a.id}`}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && <ApplicationDetail app={viewing} onClose={() => setViewing(null)} onAct={updateStatus} />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const c = { emerald: "text-emerald-400 border-emerald-500/20 bg-emerald-500/[0.05]", amber: "text-amber-400 border-amber-500/20 bg-amber-500/[0.05]", red: "text-red-400 border-red-500/20 bg-red-500/[0.05]", sky: "text-sky-400 border-sky-500/20 bg-sky-500/[0.05]" }[tone] || "text-white border-white/10 bg-white/[0.03]";
  return (
    <div className={`rounded-xl border p-3 lg:p-4 ${c}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] font-bold uppercase tracking-wider truncate">{label}</div>
      </div>
      <div className="text-xl lg:text-2xl font-black mt-1 truncate">{value}</div>
    </div>
  );
}

function PaymentBadge({ status }) {
  const s = (status || "").toLowerCase();
  const style = s === "paid" ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
              : s === "failed" ? "bg-red-500/15 border-red-500/30 text-red-300"
              : "bg-amber-500/15 border-amber-500/30 text-amber-300";
  return <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${style}`}>{s || "pending"}</span>;
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const style = s === "approved" ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
              : s === "rejected" ? "bg-red-500/15 border-red-500/30 text-red-300"
              : s === "pending" ? "bg-amber-500/15 border-amber-500/30 text-amber-300"
              : "bg-white/5 border-white/10 text-white/60";
  return <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${style}`}>{s.replace("_"," ") || "-"}</span>;
}

function ApplicationDetail({ app, onClose, onAct }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-[#0f1620] rounded-2xl border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black">{app.name}</h2>
            <div className="text-sm text-white/50">{app.email} · {app.phone}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="detail-close">
            <XCircle className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm mb-5">
          <Info label="Username" value={app.username || "-"} />
          <Info label="Address" value={`${app.address || "-"}${app.city ? `, ${app.city}` : ""}${app.state ? `, ${app.state}` : ""}`} />
          <Info label="Telegram" value={app.telegram || "-"} />
          <Info label="WhatsApp" value={app.whatsapp || "-"} />
          <Info label="Payment" value={`৳${Number(app.final_amount || app.paid_amount || 13000).toLocaleString("en-IN")} via ${app.gateway_method?.toUpperCase() || "-"}`} />
          <Info label="Coupon" value={app.coupon_code ? `${app.coupon_code} (-৳${Number(app.coupon_discount||0).toLocaleString("en-IN")})` : "None"} />
          <Info label="Payment Status" value={<PaymentBadge status={app.payment_status} />} />
          <Info label="Application Status" value={<StatusBadge status={app.status} />} />
        </div>

        {app.message && (
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 mb-4">
            <div className="text-xs font-bold text-white/60 mb-1">Message</div>
            <div className="text-sm text-white/85">{app.message}</div>
          </div>
        )}

        {/* Documents */}
        <div className="text-xs font-bold text-white/60 mb-2">Documents</div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <DocPreview url={app.nid_front_url} label="NID Front" />
          <DocPreview url={app.nid_back_url} label="NID Back" />
          <DocPreview url={app.holding_photo_url} label="Holding" />
        </div>

        {app.status === "pending" && app.payment_status === "paid" && (
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-white/5">
            <button onClick={() => onAct(app.id, "rejected")} className="px-4 py-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white text-sm font-bold" data-testid="detail-reject">Reject</button>
            <button onClick={() => onAct(app.id, "approved")} className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold" data-testid="detail-approve">Approve</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm text-white mt-0.5">{value}</div>
    </div>
  );
}

function DocPreview({ url, label }) {
  if (!url) return <div className="aspect-square rounded-lg bg-white/[0.02] border border-white/5 flex items-center justify-center text-[10px] text-white/30">{label} — missing</div>;
  const src = url.startsWith("http") ? url : `${BACKEND}${url}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg bg-black/40 border border-white/10 overflow-hidden relative group">
      <img src={src} alt={label} className="w-full h-full object-cover" />
      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-white/80 px-2 py-1 font-medium">{label}</div>
    </a>
  );
}

/* ═════════════════════════ Coupons Tab ═════════════════════════ */
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", label: "", discount_type: "percent", discount_value: 10, enabled: true, usage_limit: 0, expires_at: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/admin/agent-coupons`, getAuth());
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.post(`${BACKEND}/api/admin/agent-coupons`, form, getAuth());
      toast.success("Saved");
      setForm({ code: "", label: "", discount_type: "percent", discount_value: 10, enabled: true, usage_limit: 0, expires_at: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/agent-coupons/${code}`, getAuth());
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* Create/edit form */}
      <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-[#0f1620] p-5 space-y-3">
        <div className="text-sm font-bold text-white/85 flex items-center gap-2"><Plus className="w-4 h-4" /> Add / Update Coupon</div>
        <F label="Code (auto-uppercased)" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="WELCOME20" testId="coupon-code" />
        <F label="Label / description" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="Welcome bonus" testId="coupon-label" />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-xs text-white/60 mb-1">Discount type</div>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="w-full rounded-xl bg-[#0a1119] border border-white/10 px-3 py-2.5 text-sm" data-testid="coupon-type">
              <option value="percent">Percent (%)</option>
              <option value="flat">Flat BDT</option>
            </select>
          </div>
          <F label="Value" type="number" value={form.discount_value} onChange={(v) => setForm({ ...form, discount_value: v })} testId="coupon-value" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <F label="Usage limit (0 = unlimited)" type="number" value={form.usage_limit} onChange={(v) => setForm({ ...form, usage_limit: v })} testId="coupon-limit" />
          <F label="Expires at (ISO, optional)" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} placeholder="2026-12-31T23:59:59Z" testId="coupon-expires" />
        </div>
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="coupon-enabled" />
          Enabled
        </label>
        <button onClick={save} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold" data-testid="coupon-save">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>

      {/* List */}
      <div className="lg:col-span-3 rounded-2xl border border-white/5 bg-[#0f1620] p-5">
        <div className="text-sm font-bold text-white/85 mb-3">Active coupons</div>
        {loading ? <div className="text-white/40 text-sm">Loading...</div> : items.length === 0 ? (
          <div className="text-white/40 text-sm py-8 text-center">No coupons yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((c) => (
              <div key={c.code} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid={`coupon-row-${c.code}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
                  <Ticket className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-black text-white">{c.code}</div>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${c.enabled ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-white/50"}`}>
                      {c.enabled ? "ACTIVE" : "OFF"}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/50 truncate">
                    {c.label || (c.discount_type === "percent" ? `${c.discount_value}% off` : `৳${c.discount_value} off`)}
                    {" · "} Used {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}
                  </div>
                </div>
                <button onClick={() => del(c.code)} className="text-xs text-red-400 hover:text-red-300 p-2" data-testid={`coupon-del-${c.code}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function F({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <div className="text-xs text-white/60 mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
             className="w-full rounded-xl bg-[#0a1119] border border-white/10 focus:border-emerald-500/50 outline-none px-3 py-2.5 text-sm placeholder-white/30"
             data-testid={testId} />
    </div>
  );
}

/* ═════════════════════════ Gateways Tab ═════════════════════════ */
function GatewaysTab() {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/admin/agent-apply/gateway-settings`, getAuth());
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (id, field, val) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, [field]: val } : it));
    setDirty(true);
  };
  const move = (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    // Renumber priority: top item highest
    next.forEach((it, i) => { it.priority = 1000 - i; });
    setItems(next);
    setDirty(true);
  };
  const save = async () => {
    try {
      await axios.post(`${BACKEND}/api/admin/agent-apply/gateway-settings`, { items }, getAuth());
      toast.success("Gateway order & visibility saved");
      setDirty(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="rounded-2xl border border-white/5 bg-[#0f1620] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-bold text-white/85">Gateway ordering & visibility (Agent Application page)</div>
          <div className="text-xs text-white/50 mt-0.5">Top → shown first · toggle to hide from the agent-application page only</div>
        </div>
        <button onClick={save} disabled={!dirty}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                  dirty ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-white/5 text-white/40 cursor-not-allowed"
                }`}
                data-testid="gateways-save">
          <Save className="w-4 h-4" /> Save order
        </button>
      </div>

      {loading ? <div className="text-white/40 text-sm">Loading...</div> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className={`flex items-center gap-3 rounded-xl border p-3 ${it.agent_apply_enabled ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-white/[0.01] opacity-60"}`} data-testid={`gw-row-${it.id}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="w-6 h-5 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-30" data-testid={`gw-up-${it.id}`}>
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="w-6 h-5 flex items-center justify-center rounded hover:bg-white/10 disabled:opacity-30" data-testid={`gw-down-${it.id}`}>
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-[11px] font-black text-emerald-300 uppercase">
                {(it.provider || "?").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white truncate">{it.label}</div>
                <div className="text-[11px] text-white/45 truncate">{(it.methods || []).join(" · ") || "—"}{!it.site_enabled && " · SITE-DISABLED"}</div>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-white/70">
                <input type="checkbox" checked={!!it.recommended} onChange={(e) => patch(it.id, "recommended", e.target.checked)} data-testid={`gw-rec-${it.id}`} />
                Recommended
              </label>
              <label className="flex items-center gap-1.5 text-xs text-white/70">
                <input type="checkbox" checked={!!it.agent_apply_enabled} onChange={(e) => patch(it.id, "agent_apply_enabled", e.target.checked)} data-testid={`gw-en-${it.id}`} />
                Show
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
